import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Check, File, X } from 'lucide-react'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { CharacterRole } from 'app/frontend/types/custom'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import { resolveSelectionOverrideDisplay } from './eventSelectionOverrideDisplay'
import { getSpecializationsByClass } from 'app/frontend/base/services/characterValues'
import Card from 'app/frontend/base/components/Card'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { imageUrl } from 'app/frontend/base/helpers'
import { capitalize } from 'lodash'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import ViewerParticipantAssignmentAbilitiesDialog from './ViewerParticipantAssignmentAbilitiesDialog'
import { cn } from '@/lib/utils'
import {
  EventPlanOverridesByPlanId,
  getEncounterAssignmentAbilityHidden,
  getPlanEncounterAssignmentOverrides,
} from './eventActivityPlanOverrides'

type Props = {
  entry: EventActivityParticipantEntry
  activeActivities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
}

const ViewerParticipantActivitySummary = ({
  entry,
  activeActivities,
  linkedPlanDataByActivityId,
}: Props) => {
  const [openedActivityId, setOpenedActivityId] = useState<number | null>(null)
  const isUnavailableParticipant = unavailableParticipantStatuses.includes(
    entry.participant.status,
  )
  const activityDisplayDataById = useMemo(() => {
    const map = new Map<
      number,
      {
        displayCharacter: ReturnType<typeof resolveEventParticipantCharacter>
        cooldownRowCount: number
      }
    >()
    activeActivities.forEach((activity) => {
      const selection = entry.selections[activity.id]
      if (!selection) return
      const displayCharacter = resolveEventParticipantCharacter(
        entry,
        activity.id,
      )
      const linkedPlanData = linkedPlanDataByActivityId.get(activity.id)
      const actorSpellIds = (linkedPlanData?.resolvedActors ?? [])
        .filter(
          (resolvedActor) =>
            resolvedActor.participant?.characterId === displayCharacter.id,
        )
        .flatMap((resolvedActor) => resolvedActor.actor.casts)
        .map((cast) => cast.spell_id)
      const generalSpellIds = (
        linkedPlanData?.linkedRaidPlan?.timeline.general?.casts ?? []
      ).map((cast) => cast.spell_id)
      const cooldownRowCount = new Set(
        [...actorSpellIds, ...generalSpellIds].map((spellId) =>
          spellId === null ? 'spell:null' : `spell:${spellId}`,
        ),
      ).size
      map.set(activity.id, {
        displayCharacter,
        cooldownRowCount,
      })
    })
    return map
  }, [activeActivities, entry, linkedPlanDataByActivityId])

  return (
    <div className='flex h-full min-h-0 min-w-0 max-w-full flex-wrap content-stretch items-stretch gap-2'>
      {activeActivities.map((activity) => {
        const selection = entry.selections[activity.id]
        if (!selection) return null
        const activityDisplayData = activityDisplayDataById.get(activity.id)
        if (!activityDisplayData) return null
        const isSelected = !isUnavailableParticipant && selection.selected
        const { displayCharacter, cooldownRowCount } = activityDisplayData
        const selectionOverrideDisplay = resolveSelectionOverrideDisplay({
          classId: displayCharacter.classId,
          role: selection.role,
          specId: selection.specId,
          mainRole: entry.role,
          mainSpecId: entry.specId,
        })
        const hasCharacterOverride = selection.characterId !== entry.characterId
        const linkedPlanData = linkedPlanDataByActivityId.get(activity.id)
        const encounterAssignmentOverrides =
          getPlanEncounterAssignmentOverrides(
            activity.planOverrides as EventPlanOverridesByPlanId,
            linkedPlanData?.linkedRaidPlan?.id,
          )
        const assignmentAbilities =
          linkedPlanData?.resolvedAssignmentAbilitiesByCharacterId[
            displayCharacter.id
          ]?.filter(
            (assignmentAbility) =>
              !getEncounterAssignmentAbilityHidden({
                encounterAssignments: encounterAssignmentOverrides,
                spellId: assignmentAbility.spellId,
              }),
          ) ?? []
        const assignedSpellIds = assignmentAbilities.map(
          (assignmentAbility) => assignmentAbility.spellId,
        )
        const totalAbilityCount = assignmentAbilities.length + cooldownRowCount

        return (
          <Card
            key={activity.id}
            className='self-stretch flex flex-col items-center gap-1.5 p-0'
          >
            <Tooltip>
              <TooltipTrigger className='inline-flex'>
                <div className='relative'>
                  <img
                    src={
                      activity.encounter?.thumbnailUrl ??
                      activity.instance?.tileUrl ??
                      imageUrl(
                        `activity-thumbnails/${activity.activityType}.jpg`,
                      )
                    }
                    className='w-10 h-10 rounded-t-md border-b border-border'
                  />
                  <div className='absolute bottom-px right-1 text-sm font-semibold px-1 py-0.5 leading-none rounded-t-[8px] bg-card-nested/70'>
                    {activity.difficulty?.[0].toUpperCase() ?? '-'}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {activity.encounter?.name ||
                  activity.instance?.name ||
                  capitalize(activity.activityType)}
              </TooltipContent>
            </Tooltip>

            <Badge variant='outline' className='h-6 px-1.5'>
              {isSelected ? (
                <Check className='w-5 h-5 text-success-foreground' />
              ) : (
                <X className='w-5 h-5 text-destructive-foreground' />
              )}
            </Badge>

            {isSelected ? (
              <span className='flex items-center justify-center'>
                {hasCharacterOverride ? (
                  <Tooltip>
                    <TooltipTrigger className='inline-flex'>
                      <span className='relative inline-flex'>
                        <CharacterClassIcon
                          characterClass={displayCharacter.characterClass}
                        />
                        <span className='absolute -right-2 -bottom-2 rounded-full bg-card p-0.5'>
                          <CharacterRoleIcon
                            role={selection.role as CharacterRole}
                            size='small'
                          />
                        </span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      className={
                        classColours[displayCharacter.characterClass].text
                      }
                    >
                      {displayCharacter.name}
                    </TooltipContent>
                  </Tooltip>
                ) : selectionOverrideDisplay.showSpecIcon &&
                  selectionOverrideDisplay.specIcon ? (
                  <BlizzardIcon iconName={selectionOverrideDisplay.specIcon} />
                ) : selectionOverrideDisplay.showRoleIcon ? (
                  <CharacterRoleIcon role={selection.role as CharacterRole} />
                ) : selection.specId ? (
                  <BlizzardIcon
                    iconName={
                      getSpecializationsByClass(displayCharacter.classId)[
                        selection.specId
                      ]?.icon
                    }
                  />
                ) : (
                  <CharacterRoleIcon role={selection.role as CharacterRole} />
                )}
              </span>
            ) : (
              <span className='h-4' />
            )}

            {activity.activityType === 'raids' && isSelected ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type='button'
                      onClick={() => setOpenedActivityId(activity.id)}
                      className='inline-flex bg-transparent border-0 p-0'
                    >
                      <Badge
                        variant='outline'
                        className={cn(
                          'px-1.5 gap-0.5 mb-1 font-normal',
                          totalAbilityCount === 0
                            ? 'text-muted-foreground'
                            : 'text-accent-foreground',
                        )}
                      >
                        <File className='w-3 h-3' />{' '}
                        <span>{totalAbilityCount}</span>
                      </Badge>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>View my assignments</TooltipContent>
                </Tooltip>
                <ViewerParticipantAssignmentAbilitiesDialog
                  activity={activity}
                  open={openedActivityId === activity.id}
                  onOpenChange={(open) =>
                    setOpenedActivityId(open ? activity.id : null)
                  }
                  linkedPlanData={linkedPlanData}
                  assignedSpellIds={assignedSpellIds}
                  characterId={displayCharacter.id}
                />
              </>
            ) : null}
          </Card>
        )
      })}
    </div>
  )
}

export default ViewerParticipantActivitySummary
